import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../redux';
import { updateGeoChartStyleOptions } from '../../../../redux/ChartPoperties/ChartControlsActions';
import SwitchWithInput from '../../SwitchWithInput';
import { Checkbox, TextField, Tooltip, Typography } from '@mui/material';
import InputPositiveNumber from '../../CommonFunctions/InputPositiveNumber';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';

const separatorOptions = [
  { type: 'None', value: 'None' },
  { type: 'Comma', value: 'Comma' },
  { type: 'Abbrev', value: 'Abbrev' },
];

const formatOptions: any[] = [
  { type: 'Number', value: 'Number' },
  { type: 'Currency', value: 'Currency' },
  { type: 'Percent', value: 'Percent' },
];

function VisualScaleSeparators({
  iconColor,
  backgroundColor,
}: {
  iconColor?: string;
  backgroundColor?: string;
}) {
  const dispatch = useDispatch();

  const { tabTileProps, chartControls } = useSelector((state: RootState) => ({
    tabTileProps: state.tabTileProps,
    chartControls: state.chartControls,
  }));

  const propKey = `${tabTileProps.selectedTabId}.${tabTileProps.selectedTileId}`;
  const geoStyle = chartControls.properties[propKey]?.geoChartControls || {};

  const [selectedSeparator, setSelectedSeparator] = useState('None');

  useEffect(() => {
    if (geoStyle?.isDivergentFormat) {
      setSelectedSeparator(geoStyle.isDivergentFormat);
    }
  }, [geoStyle?.isDivergentFormat]);

  const showVisualScale = geoStyle.showVisualScale ?? false;
  const selectedFormat = geoStyle.divergentFormatValue ?? 'Number';
  const isRoundOff = geoStyle.enableRounding ?? true;
  const roundingValue = geoStyle.roundingDigits ?? 1;
  const percentType = geoStyle.percentType ?? 'NonCalculated';

  const renderFormatOptions = () => {
    return formatOptions.map((item) => {
      const isSelected = item.value === selectedFormat;

      return (
        <div
          key={item.value}
          className="radioButton"
          style={{
            backgroundColor: isSelected ? 'rgba(224, 224, 224, 1)' : 'white',
            cursor: isSelected ? 'default' : 'pointer',
            fontWeight: isSelected ? '600' : 'normal',
          }}
          onClick={() => {
            if (isSelected) return;

            dispatch(updateGeoChartStyleOptions(propKey, 'divergentFormatValue', item.value));
          }}
        >
          {item.type}
        </div>
      );
    });
  };

  return (
    <div className="optionsInfo">
      {/* Show Scale */}
      {/* <div className="optionDescription">Show Scale</div>
      <div style={{ width: '50%', marginTop: '3px', marginLeft: '35px' }}>
        <SwitchWithInput
          isChecked={showVisualScale}
          onSwitch={() =>
            dispatch(updateGeoChartStyleOptions(propKey, 'showVisualScale', !showVisualScale))
          }
        />
      </div> */}
      <div
        className="optionDescription"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          color: iconColor ? iconColor : '#404040',
        }}
      >
        <span>SHOW SCALE</span>
        <div style={{ transform: 'translateX(60px)' }}>
          <SwitchWithInput
            isChecked={showVisualScale}
            onSwitch={() =>
              dispatch(updateGeoChartStyleOptions(propKey, 'showVisualScale', !showVisualScale))
            }
          />
        </div>
      </div>

      {showVisualScale && (
        <>
          <div
            className="optionDescription"
            style={{ marginTop: '5px', color: iconColor ? iconColor : '#404040' }}
          >
            Format Value
          </div>
          <div className="radioButtons" style={{ marginTop: '3px' }}>
            {renderFormatOptions()}
          </div>

          {selectedFormat === 'Currency' && (
            <div>
              <div
                className="optionDescription "
                style={{ marginTop: '-2px', color: iconColor ? iconColor : '#404040' }}
              >
                Currency Symbol
              </div>
              <div
                style={{
                  padding: '0',
                  marginTop: '0px',
                  marginBottom: '8px',
                  marginLeft: '5px',
                  marginRight: '5px',
                }}
              >
                <TextField
                  id="currency-symbol-input"
                  variant="outlined"
                  value={geoStyle.currencySymbol ?? '$'}
                  onChange={(e) =>
                    dispatch(updateGeoChartStyleOptions(propKey, 'currencySymbol', e.target.value))
                  }
                  sx={{
                    width: '100%',
                    '& .MuiOutlinedInput-root': {
                      height: '26px',
                      backgroundColor: 'white',
                      fontSize: '13px',
                      '& fieldset': {
                        border: '1px solid rgb(211, 211, 211)',
                      },
                      '&:hover fieldset': {
                        border: '1px solid #2bb9bb',
                      },
                      '&.Mui-focused fieldset': {
                        border: '1px solid #2bb9bb',
                      },
                    },
                    '& .MuiOutlinedInput-input': {
                      padding: '0 8px',
                    },
                  }}
                />
              </div>
            </div>
          )}

          {selectedFormat === 'Percent' && (
            <div>
              <div
                className="optionDescription "
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  marginTop: '0px',
                  color: iconColor ? iconColor : '#404040',
                }}
              >
                <span> Percent Type </span>

                <Tooltip
                  placement="top"
                  arrow
                  title={
                    <div>
                      <p style={{ margin: 0, padding: 0 }}>
                        Calculated: Multiplies by 100 and appends the % symbol.
                      </p>
                      <p style={{ margin: 0, padding: 0 }}>Non Calculated: Appends the % symbol.</p>
                    </div>
                  }
                  slotProps={{
                    tooltip: {
                      sx: {
                        fontSize: '11px',
                      },
                    },
                  }}
                >
                  <InfoOutlineIcon fontSize="small" />
                </Tooltip>
              </div>
              <div className="radioButtons" style={{ marginTop: '3px' }}>
                <div
                  className={
                    // percentType === 'Calculated' ? 'radioButtonSelected' : 'radioButton'
                    percentType === 'Calculated' ? 'radioButton' : 'radioButtonSelected'
                  }
                  style={{
                    backgroundColor:
                      percentType === 'Calculated' ? 'rgba(224, 224, 224, 1)' : 'white',
                    cursor: percentType === 'Calculated' ? 'default' : 'pointer',
                    fontWeight: percentType === 'Calculated' ? '600' : 'normal',
                  }}
                  onClick={() => {
                    if (percentType === 'Calculated') return;
                    dispatch(updateGeoChartStyleOptions(propKey, 'percentType', 'Calculated'));
                  }}
                >
                  Calculated
                </div>
                <div
                  className={
                    // percentType === 'NonCalculated' ? 'radioButtonSelected' : 'radioButton'
                    percentType === 'NonCalculated' ? 'radioButton' : 'radioButtonSelected'
                  }
                  style={{
                    backgroundColor:
                      percentType === 'NonCalculated' ? 'rgba(224, 224, 224, 1)' : 'white',
                    cursor: percentType === 'NonCalculated' ? 'default' : 'pointer',
                    fontWeight: percentType === 'NonCalculated' ? '600' : 'normal',
                  }}
                  onClick={() => {
                    if (percentType === 'NonCalculated') return;
                    dispatch(updateGeoChartStyleOptions(propKey, 'percentType', 'NonCalculated'));
                  }}
                >
                  Non Calculated
                </div>
              </div>
            </div>
          )}

          <div
            className="optionDescription"
            style={{ marginTop: '0px', color: iconColor ? iconColor : '#404040' }}
          >
            Separator
          </div>

          <div className="radioButtons" style={{ marginTop: '3px' }}>
            {separatorOptions.map((item) => {
              const isSelected = item.value === selectedSeparator;

              return (
                <div
                  key={item.value}
                  className="radioButton"
                  style={{
                    backgroundColor: isSelected ? 'rgba(224, 224, 224, 1)' : 'white',
                    cursor: isSelected ? 'default' : 'pointer',
                    fontWeight: isSelected ? 600 : 400,
                  }}
                  onClick={() => {
                    if (isSelected) return;
                    setSelectedSeparator(item.value);
                    dispatch(updateGeoChartStyleOptions(propKey, 'isDivergentFormat', item.value));
                  }}
                >
                  {item.type}
                </div>
              );
            })}
          </div>

          <div
            className="optionDescription"
            style={{
              marginTop: '-3px',
              marginBottom: '0px',
              color: iconColor ? iconColor : '#404040',
            }}
          >
            Round Off
          </div>
          <div
            className="optionDescription"
            style={{
              paddingTop: '0px',
              paddingBottom: '0px',
              marginTop: '-7px',
              paddingLeft: '4px',
              paddingRight: '8px',
              display: 'flex',
              alignItems: 'center',
              backgroundColor: backgroundColor || 'white',
              color: iconColor ? iconColor : '#404040',
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowUp' && roundingValue >= 4) {
                e.preventDefault();
              }
              if (e.key === 'ArrowDown' && roundingValue <= 0) {
                e.preventDefault();
              }
            }}
          >
            <Checkbox
              checked={!isRoundOff}
              onChange={() =>
                dispatch(updateGeoChartStyleOptions(propKey, 'enableRounding', !isRoundOff))
              }
              sx={{
                color: iconColor || '#a6a6a6',
                '&.Mui-checked': {
                  color: iconColor || '#a6a6a6',
                },
                '&:hover': {
                  backgroundColor: 'transparent !important',
                },
              }}
            />

            {/* <InputPositiveNumber
              value={roundingValue}
              disabled={isRoundOff}
              updateValue={(value: number) =>
                dispatch(updateGeoChartStyleOptions(propKey, 'roundingDigits', value))
              }
            /> */}
            <InputPositiveNumber
              value={roundingValue}
              disabled={isRoundOff}
              updateValue={(value: number) => {
                let newValue = value > 4 ? 4 : value;
                if (newValue < 0) newValue = 0;
                dispatch(updateGeoChartStyleOptions(propKey, 'roundingDigits', newValue));
              }}
              fromFormats={true}
              iconColor={iconColor}
              backgroundColor={backgroundColor}
            />

            <Typography sx={{ color: iconColor ? iconColor : '#404040' }}>decimal</Typography>
          </div>
        </>
      )}
    </div>
  );
}

export default VisualScaleSeparators;
